<template>
  <div class="upcoming-sites">
    <table-toolbar
      :show-title="true"
      title="Upcoming Sites"
      :show-count="true"
      :current-count="paginatedSites.length"
      :total-count="filteredSites.length"
      :searchable="false"
      :show-per-page-selector="false"
      :show-refresh="false"
      :show-column-visibility="false"
      :show-view-toggle="false"
    >
      <template #custom-controls>
        <themed-select
          v-model="dueInMonths"
          :options="dueInOptions"
          size="sm"
          aria-label="Filter by due date"
        />
      </template>
      
      <template #action-buttons>
        <themed-button 
          variant="default" 
          size="sm" 
          @click="viewAll"
          aria-label="View all sites"
        >
          View All
        </themed-button>
      </template>
    </table-toolbar>
    
    <themed-table
      :data="paginatedSites"
      :columns="tableColumns"
      :pagination="true"
      :per-page="perPage"
      :current-page="currentPage"
      :total-items="filteredSites.length"
      @page-change="currentPage = $event"
      :searchable="false"
      :hoverable="true"
      aria-label="Upcoming Sites"
      @row-click="handleRowClick"
    />
  </div>
</template>

<script>
import ThemedTable from '../ThemedTable.vue';
import ThemedButton from '../ThemedButton.vue';
import ThemedSelect from '../ThemedSelect.vue';
import TableToolbar from '../table/TableToolbar.vue';

export default {
  name: 'UpcomingSites',
  
  components: {
    ThemedTable,
    ThemedButton,
    ThemedSelect,
    TableToolbar
  },
  
  props: {
    sites: {
      type: Array,
      required: true
    },
    showClientInfo: {
      type: Boolean,
      default: true
    }
  },
  
  data() {
    return {
      dueInMonths: 24,
      perPage: 5,
      currentPage: 1,
      dueInOptions: [
        { value: 6, label: 'Due within 6 months' },
        { value: 12, label: 'Due within 1 year' },
        { value: 24, label: 'Due within 2 years' },
        { value: 60, label: 'Due within 5 years' }
      ]
    };
  },
  
  computed: {
    tableColumns() {
      const columns = [
        {
          field: 'building.address_line_1',
          label: 'Address',
          type: 'text'
        },
        {
          field: 'building.city',
          label: 'City',
          type: 'text',
          width: '150px'
        }
      ];
      
      if (this.showClientInfo) {
        columns.push({
          field: 'client.name',
          label: 'Client',
          type: 'text',
          width: '150px'
        });
      }
      
      columns.push(
        {
          field: 'lease_end_date',
          label: 'Lease End',
          type: 'date',
          width: '120px'
        },
        {
          field: 'due_in_text',
          label: 'Due In',
          type: 'text',
          width: '120px',
          badge: true,
          badgeClass: (value, row) => {
            // Return badge class based on urgency
            if (!row.lease_end_date) return 'badge--secondary';
            
            const now = new Date();
            const leaseEnd = new Date(row.lease_end_date);
            const monthsRemaining = (leaseEnd - now) / (1000 * 60 * 60 * 24 * 30);
            
            if (monthsRemaining <= 6) return 'badge--red';
            if (monthsRemaining <= 12) return 'badge--yellow';
            if (monthsRemaining <= 24) return 'badge--blue';
            return 'badge--green';
          }
        }
      );
      
      return columns;
    },
    
    filteredSites() {
      if (!this.sites || this.sites.length === 0) {
        return [];
      }
      
      const now = new Date();
      const monthsInMs = this.dueInMonths * 30 * 24 * 60 * 60 * 1000;
      const cutoffDate = new Date(now.getTime() + monthsInMs);
      
      return this.sites.filter(site => {
        if (!site.lease_end_date) return false;
        const leaseEndDate = new Date(site.lease_end_date);
        return leaseEndDate >= now && leaseEndDate <= cutoffDate;
      }).sort((a, b) => {
        const dateA = new Date(a.lease_end_date);
        const dateB = new Date(b.lease_end_date);
        return dateA - dateB;
      });
    },
    
    paginatedSites() {
      const start = (this.currentPage - 1) * this.perPage;
      const end = start + this.perPage;
      return this.filteredSites.slice(start, end);
    }
  },
  
  watch: {
    dueInMonths() {
      this.currentPage = 1;
    }
  },
  
  methods: {
    viewAll() {
      window.location.href = '/sites';
    },
    
    handleRowClick({ row }) {
      window.location.href = `/sites/${row.id}`;
    }
  }
};
</script>
